import React, { useState } from 'react';
import { ButtonSwitcher, ButtonSwitcherProps } from 'components/button-switcher';
import { FiltersButton, FiltersDialog } from 'components/filters-dialog';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import { PATIENT_MEETING_SWITCH } from '../../helpers';
import style from './index.module.scss';

interface Props {
  value: PATIENT_MEETING_SWITCH;
  /** The grid's first period: what the filters dialog's "clear" restores. */
  defaultValue: PATIENT_MEETING_SWITCH;
  options: ButtonSwitcherProps<PATIENT_MEETING_SWITCH>['options'];
  onChange: (value: PATIENT_MEETING_SWITCH) => void;
  /** The toolbar's Stack spaces its items with an inline style. */
  style?: React.CSSProperties;
}

/**
 * A meeting grid's period (all / upcoming / past). Desktop: the segmented
 * switch in the grid's toolbar, applied as it changes. Below md (N1, brief
 * pattern B): the one filter in the grid's filters dialog, edited as a draft --
 * "apply" sets it, "clear" puts the draft back to `defaultValue`, closing
 * drops it.
 */
export const PeriodFilter: React.FC<Props> = ({
  value,
  defaultValue,
  options,
  onChange,
  style: inlineStyle,
}) => {
  const isBelowMd = useIsBelowMd();
  // null while the dialog is closed
  const [draft, setDraft] = useState<PATIENT_MEETING_SWITCH | null>(null);

  if (!isBelowMd) {
    return (
      <ButtonSwitcher value={value} onChange={onChange} options={options} style={inlineStyle} />
    );
  }

  const onApply = () => {
    if (draft !== null) onChange(draft);
    setDraft(null);
  };

  return (
    <>
      <FiltersButton count={value === defaultValue ? 0 : 1} onClick={() => setDraft(value)} />
      <FiltersDialog
        open={draft !== null}
        onClose={() => setDraft(null)}
        onApply={onApply}
        onClear={() => setDraft(defaultValue)}
      >
        <ButtonSwitcher
          value={draft ?? value}
          onChange={setDraft}
          options={options}
          className={style.switcher}
        />
      </FiltersDialog>
    </>
  );
};
